<template>
  <div class="project-edit-container">
    <el-card class="edit-card" v-loading="loading">
      <template v-if="loading">
        <div style="text-align: center; padding: 50px">
          <el-skeleton :rows="5" animated />
        </div>
      </template>
      <template v-else>
        <!-- 基本信息编辑 -->
        <div style="margin-top: 30px">
          <DataProjectBasicInfo 
            v-model="form" 
            :departments="departments"
            :show-reporters="false"
            :show-watchers="false"
            :allow-past-date="true"
            :multiple-template="isCreate"
          />
          
          <!-- 年份选择 -->
          <el-form label-width="110px" style="margin-top: 20px">
            <el-form-item label="数据年份">
              <el-select v-model="form.year" placeholder="请选择年份" style="width: 200px">
                <el-option
                  v-for="year in yearOptions"
                  :key="year"
                  :label="`${year}年`"
                  :value="year"
                />
              </el-select>
            </el-form-item>
          </el-form>
          
          <el-divider />
          <div class="form-actions">
            <el-button type="primary" @click="submitForm">保存</el-button>
            <el-button @click="cancel">取消</el-button>
          </div>
        </div>
      </template>
    </el-card>
  </div>
</template>

<script>
import {
  getDataProject,
  createDataProject,
  updateDataProject,
} from "@/api/report-system/dataProject";
import { getDepartmentStructure } from "@/api/user-management/department";
import DataProjectBasicInfo from "@/components/DataProjectBasicInfo.vue";

export default {
  name: "ProjectEdit",
  components: {
    DataProjectBasicInfo,
  },
  data() {
    return {
      form: {
        id: null,
        name: "",
        type: "",
        description: "",
        status: "active",
        periodic: false,
        // 默认留空：报送周期现在是所有项目的必填分类项，预设 weekly 会让
        // validateForm 里的必填校验永远走不到，用户不点选就被静默归入「周报」
        period: "",
        week_days: "",
        month_days: "",
        start_date: null,
        schedule_at: null,
        end_date: null,
        template_id: null,
        template_ids: [],
        project_owner_id: null,
        dialog_department: [],
        year: new Date().getFullYear(),
      },
      departments: [],
      loading: false,
      yearOptions: [],
    };
  },
  computed: {
    // 用路由参数判断，不能用 form.id——loadProject 是异步的，
    // 编辑场景首帧 form.id 还是 null，会先闪一下多选框
    isCreate() {
      return !this.$route.params.id;
    },
  },
  created() {
    this.yearOptions = this.buildYearOptions();
    this.loadDepartments();
    if (this.$route.params.id) {
      this.loadProject(this.$route.params.id);
    }
  },
  methods: {
    async loadDepartments() {
      try {
        const response = await getDepartmentStructure();
        this.departments = response.data;
      } catch (error) {
        console.error("加载旗县列表失败:", error);
        this.$message.error("加载旗县列表失败");
      }
    },
    async loadProject(id) {
      this.loading = true;
      try {
        await this.loadDepartments();
        const response = await getDataProject(id);
        const projectData = response.data.project;
        
        this.form = {
          ...projectData,
        };
      } catch (error) {
        console.error("加载项目数据失败:", error);
        this.$message.error("加载项目数据失败");
      } finally {
        this.loading = false;
      }
    },
    validateForm() {
      if (!this.form.name || this.form.name.trim() === "") {
        this.$message.error("项目名称不能为空");
        return false;
      }

      if (this.isCreate) {
        if (!this.form.template_ids || this.form.template_ids.length === 0) {
          this.$message.error("请至少选择一个数据模板");
          return false;
        }
      } else if (!this.form.template_id) {
        this.$message.error("请选择数据模板");
        return false;
      }

      if (!this.form.start_date) {
        this.$message.error("请选择开始日期");
        return false;
      }

      if (!this.form.schedule_at) {
        this.$message.error("请选择执行时间");
        return false;
      }

      if (this.form.type === "periodic" && !this.form.end_date) {
        this.$message.error("请选择结束日期");
        return false;
      }

      if (
        this.form.type === "periodic" &&
        new Date(this.form.end_date) < new Date(this.form.start_date)
      ) {
        this.$message.error("结束日期不能早于开始日期");
        return false;
      }

      if (!this.form.type) {
        this.$message.error("请选择项目类型");
        return false;
      }

      if (!this.form.period) {
        this.$message.error("请选择报送周期");
        return false;
      }

      if (this.form.type === "periodic") {
        if (this.form.period === "weekly" && !this.form.week_days) {
          this.$message.error("请选择星期几");
          return false;
        }

        if (this.form.period === "monthly" && !this.form.month_days) {
          this.$message.error("请选择每月几号");
          return false;
        }
      }

      if (!this.form.project_owner_id) {
        this.$message.error("请选择项目负责人");
        return false;
      }

      return true;
    },
    async submitForm() {
      if (!this.validateForm()) {
        return;
      }

      try {
        this.loading = true;
        
        // 处理 visibility_scope，确保它是对象而不是字符串
        const submitData = { ...this.form };
        if (typeof submitData.visibility_scope === 'string') {
          try {
            submitData.visibility_scope = JSON.parse(submitData.visibility_scope);
          } catch (e) {
            submitData.visibility_scope = { user_ids: [], dept_ids: [], is_public: false };
          }
        }
        
        if (!this.isCreate) {
          delete submitData.template_ids;
          await updateDataProject(this.form.id, submitData);
          this.$message.success("保存成功");
          this.$router.push("/main/data-project");
          return;
        }

        // 多选模板：每个模板建一个独立项目。
        // 必须串行——建项目会同步给每个旗县的每个填报人发短信，
        // 并行会把同样的负载堆在同一时刻。
        delete submitData.template_ids;
        const templateIds = this.form.template_ids;
        let created = 0;
        for (const templateId of templateIds) {
          try {
            await createDataProject({ ...submitData, template_id: templateId });
            created += 1;
          } catch (error) {
            console.error("创建数据项目失败:", error);
            // 失败即停止，不再继续发后续请求。
            // 把已成功的模板从选择里剔除：用户直接再点一次保存就是补建剩下的，
            // 不会把前面已经建好的又建一遍（重复建会重复下发任务、重复发短信）。
            this.form.template_ids = templateIds.slice(created);
            // 具体失败原因由 request.js 的响应拦截器弹出（errcode === -1 时会 ElMessage.error），
            // 这里只补充它不知道的进度信息。拦截器 reject 的是普通对象不是 Error，
            // 不要去读 error.message，那必然是 undefined。
            if (created > 0) {
              this.$message.warning(
                `已创建 ${created} 个项目，剩余 ${templateIds.length - created} 个未创建，已保留在模板选择中，可直接重试`
              );
            }
            return;
          }
        }

        this.$message.success(
          created > 1 ? `创建成功，共创建 ${created} 个数据项目` : "保存成功"
        );
        this.$router.push("/main/data-project");
      } catch (error) {
        this.$message.error("保存失败: " + (error.message || error));
        console.error("保存失败:", error);
      } finally {
        this.loading = false;
      }
    },
    cancel() {
      this.$router.push("/main/data-project");
    },
    buildYearOptions() {
      const currentYear = new Date().getFullYear();
      return Array.from({ length: 6 }, (_, i) => currentYear - i + 1);
    },
  },
};
</script>

<style scoped>
.project-edit-container {
  padding: 20px;
}

.edit-card {
  max-width: 900px;
  margin: 0 auto;
}

.form-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
}
</style>
